import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Redux
import { 
    getFlightEvents, 
    selectFlightEvents, 
    selectFlightEventsStatus, 
    selectFlightEventsError,
    setStartDate,
    setEndDate,
    selectFlightEventsStartDate,
    selectFlightEventsEndDate
} from '_redux/slices/timeline/flight-events';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';

// Components
import FlightEventCard from './flight-event-card';
import { CustomSpinner } from '_atoms';
import { Button } from 'react-bootstrap';

/**
 * Flight Events Component
 * Displays flight-related events and information
 */
const FlightEvents = ({ onClose }) => {
    const dispatch = useDispatch();
    const scrollContainerRef = useRef(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const prevScrollHeightRef = useRef(0);

    // Redux selectors
    const dbUser = useSelector(selectFirebaseInfo);
    const flightEvents = useSelector(selectFlightEvents);
    const status = useSelector(selectFlightEventsStatus);
    const error = useSelector(selectFlightEventsError);
    const startDate = useSelector(selectFlightEventsStartDate);
    const endDate = useSelector(selectFlightEventsEndDate);

    // Get the events array from the response data
    const events = flightEvents?.data?.events || [];

    // Set default dates on mount (last hour)
    useEffect(() => {
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

        dispatch(setStartDate(oneHourAgo.toISOString()));
        dispatch(setEndDate(now.toISOString()));

        console.log('[FlightEvents] Setting default dates (last hour):', {
            startDate: oneHourAgo.toISOString(),
            endDate: now.toISOString()
        });
    }, [dispatch]);

    // Fetch flight events when dates are set
    useEffect(() => {
        if (startDate && endDate && dbUser?.details?.authority) {
            console.log('[FlightEvents] Fetching flight events...', {
                iata: dbUser.details.authority,
                startTime: startDate,
                endTime: endDate
            });

            dispatch(getFlightEvents({
                iata: dbUser.details.authority,
                startTime: startDate,
                endTime: endDate
            }));
        }
    }, [dispatch, startDate, endDate, dbUser?.details?.authority]);

    // Log status changes
    useEffect(() => {
        //console.log('[FlightEvents] Status changed:', status);
    }, [status]);

    // Log when events data changes
    useEffect(() => {
        console.log('[FlightEvents] Events data updated:', {
            count: events?.length || 0,
            data: flightEvents
        });
    }, [flightEvents, events]);

    // Log errors
    useEffect(() => {
        if (error) {
            console.error('[FlightEvents] Error:', error);
        }
    }, [error]);

    // Handler to extend time range by 1 hour
    const handleLoadMore = () => {
        if (startDate && scrollContainerRef.current) {
            // Save current scroll height before loading
            prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
            setIsLoadingMore(true);
            
            const currentStart = new Date(startDate);
            const newStart = new Date(currentStart.getTime() - 60 * 60 * 1000);
            dispatch(setStartDate(newStart.toISOString()));
            console.log('[FlightEvents] Extended time range by 1 hour:', {
                newStartDate: newStart.toISOString()
            });
        }
    };

    // Maintain scroll position after loading more events
    useEffect(() => {
        if (isLoadingMore && status === 'succeeded' && scrollContainerRef.current) {
            const newScrollHeight = scrollContainerRef.current.scrollHeight;
            const heightDiff = newScrollHeight - prevScrollHeightRef.current;
            
            // Scroll to bottom to show the load more button and new events
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            
            setIsLoadingMore(false);
        }
    }, [status, events.length, isLoadingMore]);

    return (
        <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header info */}
            <div style={{
                marginBottom: '16px',
                color: '#999',
                fontSize: '12px'
            }}>
                <p style={{ margin: 0 }}>
                    Total Events: {flightEvents?.data?.totalEvents || 0} | 
                    Airport: {flightEvents?.data?.airport || dbUser?.details?.authority || '-'} |
                    From: {startDate ? new Date(startDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'} |
                    To: {endDate ? new Date(endDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}
                </p>
            </div>

            {/* Content */}
            <div 
                ref={scrollContainerRef}
                style={{
                    flex: 1,
                    overflowY: 'auto'
                }}
            >
                {status === 'loading' && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <CustomSpinner />
                    </div>
                )}

                {status === 'failed' && (
                    <div style={{ textAlign: 'center', color: 'red', padding: '20px' }}>
                        <p>Error loading flight events</p>
                        <p>{error}</p>
                    </div>
                )}

                {status === 'succeeded' && events.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                        <p>No flight events found for today</p>
                    </div>
                )}

                {status === 'succeeded' && events.length > 0 && (
                    <div style={{ }}>
                        {events.map((event, index) => (
                            <FlightEventCard key={event?._id || index} event={event} />
                        ))}
                    </div>
                )}

                {/* Load more button */}
                {status !== 'loading' && (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        padding: '16px',
                        marginTop: '8px'
                    }}>
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={handleLoadMore}
                        >
                            Load Previous Hour
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FlightEvents;